import { useCallback, useEffect, useState } from "react";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";

/** Kategorie, unter der die Kundenakte ihren Ordnerbaum führt. */
const KUNDENAKTE = "Alte Kundenakte";

/** Wie viele Zeilen eine Seite bringt — dasselbe Maß wie im Mail-Picker. */
export const SEITE = 50;

export type DokumentReiter = {
    zeilen: DocumentDto[];
    /** Treffer INSGESAMT laut Server (`X-Total-Count`), nicht nur die geladenen. */
    gesamt: number;
    laedt: boolean;
    fehler: string | null;
    suche: string;
    setSuche: (s: string) => void;
    kategorie: string;
    setKategorie: (k: string) => void;
    /** Es gibt noch ungeladene Treffer. */
    hatMehr: boolean;
    mehrLaden: () => void;
};

/**
 * Die Dokumentliste EINES Reiters: laden, suchen, filtern, nachladen.
 *
 * Kunden- und Vorgangsreiter unterscheiden sich nur im Filter
 * (`ohneVorgangsdokumente` / `nurVorgangsdokumente`) — der Rest ist zweimal
 * dasselbe. Der Zustand liegt bewusst je Reiter getrennt: eine Suche im einen
 * Reiter soll den anderen nicht neu laden und seine Trefferzahl nicht ändern.
 *
 * Ohne Kontakt gibt es nichts abzufragen; die Liste bleibt leer, und die
 * Fläche zeigt stattdessen den Kundenpicker. Ein Request ohne `contactId`
 * lieferte die Dokumente ALLER Kontakte.
 */
export const useDokumentReiter = (params: {
    contactId: number | null;
    /** true = Reiter „Aus Vorgängen", false = Reiter „Kundendokumente". */
    nurVorgangsdokumente: boolean;
    ausgeschlosseneKategorien: readonly string[];
    /**
     * Reiter „Kundenakte": nur die Dateien DIESES Ordners der Akte ("" = Wurzel,
     * sonst mit „/" am Ende). Mit Suchtext wird der ganze Ast darunter
     * durchsucht. Ohne Angabe (`undefined`) gilt die flache Liste wie bisher.
     */
    aktenOrdner?: string;
    /**
     * T-225: die Dokumente GENAU dieses Vorgangs (`GET /documents?vorgangId=`
     * — am Vorgang, an seinen To-dos, Gäste und Termine, mit den Rechten des
     * Vorgangs). Ist der Parameter angegeben (auch `null`), gilt nur er:
     * `contactId` wird nicht ausgewertet, und ohne Vorgang bleibt die Liste
     * leer. Ohne Angabe (`undefined`) bleibt alles wie bisher.
     */
    vorgangId?: number | null;
}): DokumentReiter => {
    const { nurVorgangsdokumente, aktenOrdner } = params;
    const vorgangsModus = params.vorgangId !== undefined;
    const vorgangId = params.vorgangId ?? null;
    // Im Vorgangsmodus zählt der Kontakt nicht — sonst lüde ein Kundenwechsel
    // in der Auswahl diese Liste umsonst neu.
    const contactId = vorgangsModus ? null : params.contactId;
    // Als Text in die Abhängigkeiten: ein Array-Literal aus dem Aufrufer ist
    // bei jedem Rendern neu und löste sonst eine Endlosschleife aus Laden und
    // Neurendern aus.
    const ausgeschlossen = params.ausgeschlosseneKategorien.join(",");

    const [suche, setSuche] = useState("");
    const debSuche = useDebouncedValue(suche, 300);
    const [kategorie, setKategorie] = useState("");
    const [offset, setOffset] = useState(0);
    const [zeilen, setZeilen] = useState<DocumentDto[]>([]);
    const [gesamt, setGesamt] = useState(0);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    // Jeder Filterwechsel fängt wieder bei Seite 1 an. Eigener Effekt, damit
    // der Ladeeffekt unten nur EINEN Fall kennt: „hol die Seite bei `offset`".
    useEffect(() => {
        setOffset(0);
        setZeilen([]);
    }, [contactId, vorgangId, debSuche, kategorie, ausgeschlossen, aktenOrdner]);

    useEffect(() => {
        if (vorgangsModus ? vorgangId == null : contactId == null) {
            setZeilen([]);
            setGesamt(0);
            setFehler(null);
            return;
        }
        const ctrl = new AbortController();
        setLaedt(true);
        setFehler(null);
        documentService
            .listPaged(
                {
                    ...(vorgangsModus ? { vorgangId: vorgangId! } : { contactId: contactId! }),
                    ...(vorgangsModus
                        ? {}
                        : aktenOrdner !== undefined
                        ? {
                              category: KUNDENAKTE,
                              ...(debSuche.trim() ? { pathUnder: aktenOrdner } : { pathPrefix: aktenOrdner }),
                          }
                        : nurVorgangsdokumente
                          ? { nurVorgangsdokumente: true }
                          : { ohneVorgangsdokumente: true }),
                    ...(debSuche.trim() ? { search: debSuche.trim() } : {}),
                    ...(aktenOrdner === undefined && kategorie ? { category: kategorie } : {}),
                    ...(ausgeschlossen ? { excludeCategory: ausgeschlossen.split(",") } : {}),
                    limit: SEITE,
                    offset,
                },
                ctrl.signal,
            )
            .then((seite) => {
                setGesamt(seite.total);
                setZeilen((vorher) => {
                    if (offset === 0) return seite.items;
                    // Beim Nachladen entdoppeln: wird währenddessen etwas
                    // hochgeladen, verschiebt sich die Seitengrenze, und
                    // dasselbe Dokument käme ein zweites Mal.
                    const bekannt = new Set(vorher.map((d) => d.id));
                    return [...vorher, ...seite.items.filter((d) => !bekannt.has(d.id))];
                });
            })
            .catch((e: unknown) => {
                if ((e as Error)?.name === "AbortError") return;
                setFehler("Dokumente konnten nicht geladen werden.");
            })
            .finally(() => {
                if (!ctrl.signal.aborted) setLaedt(false);
            });
        return () => ctrl.abort();
    }, [contactId, vorgangsModus, vorgangId, nurVorgangsdokumente, debSuche, kategorie, ausgeschlossen, offset, aktenOrdner]);

    const mehrLaden = useCallback(() => {
        setOffset((o) => o + SEITE);
    }, []);

    return {
        zeilen,
        gesamt,
        laedt,
        fehler,
        suche,
        setSuche,
        kategorie,
        setKategorie,
        hatMehr: zeilen.length < gesamt,
        mehrLaden,
    };
};
